import React, { useEffect, useRef } from 'react';
const AnimatedBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const images = {};
    const logos = ["/images/programs_logo/ncd.png", "/images/programs_logo/sickle.png", "/images/programs_logo/nikshay.png", "/images/programs_logo/uwin.png", "/images/programs_logo/pmndp.png"];
    const mouse = { x: 0, y: 0 };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const loadImages = () => {
      return Promise.all(logos.map((src, index) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            images[index] = img;
            resolve();
          };
          img.src = src;
        });
      }));
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);

    const particles = [];
    const dots = [];
    const logoCount = 12;
    const dotCount = 40;

    const createParticles = () => {
      for (let i = 0; i < logoCount; i++) {
        const isLeft = i < logoCount / 2;
        particles.push({
          x: isLeft ? Math.random() * (canvas.width * 0.3) : canvas.width * 0.7 + Math.random() * (canvas.width * 0.3),
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: 35,
          logoIndex: i % logos.length,
          opacity: 0.8,
          type: 'logo'
        });
      }
      
      // Add extra logos in left bottom corner
      for (let i = 0; i < 4; i++) {
        particles.push({
          x: Math.random() * (canvas.width * 0.25),
          y: canvas.height * 0.7 + Math.random() * (canvas.height * 0.3),
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2,
          radius: 30,
          logoIndex: i % logos.length,
          opacity: 0.7,
          type: 'logo'
        });
      }
      
      for (let i = 0; i < dotCount; i++) {
        let x, y;
        do {
          x = Math.random() * canvas.width;
          y = Math.random() * canvas.height;
        } while (x > canvas.width * 0.3 && x < canvas.width * 0.7);
        
        dots.push({
          x: x,
          y: y,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: 3,
          opacity: Math.random() * 0.6 + 0.4,
          type: 'dot'
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const allParticles = [...particles, ...dots];
      
      // Draw connections
      allParticles.forEach((particle, i) => {
        // Connect to mouse
        const mouseDistance = Math.sqrt((particle.x - mouse.x) ** 2 + (particle.y - mouse.y) ** 2);
        if (mouseDistance < 200) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(0, 91, 148, 0.15)`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      
      // Draw UHP text only when connected to network
      let hasConnection = false;
      allParticles.forEach(particle => {
        const mouseDistance = Math.sqrt((particle.x - mouse.x) ** 2 + (particle.y - mouse.y) ** 2);
        if (mouseDistance < 200) {
          hasConnection = true;
        }
      });

      allParticles.forEach((particle, i) => {
        allParticles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = `rgba(0, 91, 148, 0.15)`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        });
      });
      
      // Update and draw logos
      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < particle.radius || particle.x > canvas.width - particle.radius) {
          particle.vx *= -1;
        }
        if (particle.y < particle.radius || particle.y > canvas.height - particle.radius) {
          particle.vy *= -1;
        }

        if (images[particle.logoIndex]) {
          const logoSize = particle.radius * 1.5;
          ctx.globalAlpha = particle.opacity;
          ctx.drawImage(
            images[particle.logoIndex],
            particle.x - logoSize / 2,
            particle.y - logoSize / 2,
            logoSize,
            logoSize
          );
          ctx.globalAlpha = 1;
        }
      });

      // Update and draw dots
      dots.forEach(dot => {
        dot.x += dot.vx;
        dot.y += dot.vy;

        if (dot.x < 0 || dot.x > canvas.width) dot.vx *= -1;
        if (dot.y < 0 || dot.y > canvas.height) dot.vy *= -1;

        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100, 116, 139, ${dot.opacity})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    loadImages().then(() => {
      createParticles();
      animate();
    });

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: '50%'
      }}
    />
  );
};

export default AnimatedBackground;